import React from 'react';
import { Award, Users, Target, TrendingUp, Lightbulb, Shield } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { mockTeamMembers } from './mock';

const AboutSection = () => {
  const achievements = [
    {
      icon: Award,
      title: "IIT Alumni Expertise",
      description: "Led by distinguished alumni from premier IIT institutions with proven track records.",
      color: "from-[#5f9dff] to-[#4a8ae8]"
    },
    {
      icon: Users,
      title: "Academic Rigor + Business Savvy",
      description: "Perfect blend of theoretical knowledge and practical business acumen.",
      color: "from-[#61525a] to-[#4a3f47]"
    },
    {
      icon: Target,
      title: "Personalized Solutions",
      description: "Tailored strategies that address your unique challenges and objectives.",
      color: "from-[#ff8c19] to-[#e67c15]"
    },
    {
      icon: TrendingUp,
      title: "Proven Track Record",
      description: "Consistent success across students, startups, and established corporations.",
      color: "from-[#b4dc19] to-[#9bc216]"
    }
  ];

  const values = [
    {
      icon: Lightbulb,
      title: "Innovation-Driven",
      description: "Leveraging cutting-edge methodologies and AI-powered insights."
    },
    {
      icon: Shield,
      title: "Integrity & Excellence",
      description: "Committed to the highest standards of quality and ethical practices."
    }
  ];

  return (
    <section id="about" className="py-20 bg-gradient-to-br from-[#f7f5f2] via-white to-[#f7f5f2]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e1919] mb-6 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
            About
            <span className="bg-gradient-to-r from-[#5f9dff] to-[#61525a] bg-clip-text text-transparent"> Knowledge Ninja Lab</span>
          </h2>
          <p className="text-lg text-[#736c64] max-w-4xl mx-auto leading-relaxed">
            We are an alumni-driven initiative by a team from IIT, working at the intersection of education, 
            market research, AI/analytics projects, and mentorship. Our mission is to empower students, startups, 
            and corporates with deep research insights, actionable intelligence, and strategic growth support.
          </p>
        </div>

        {/* Mission Statement */}
        <div className="bg-gradient-to-r from-[#5f9dff]/5 to-[#61525a]/5 rounded-3xl p-8 mb-16 backdrop-blur-sm border border-white/50">
          <div className="text-center">
            <h3 className="text-2xl font-bold text-[#1e1919] mb-4 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
              Our Mission
            </h3>
            <p className="text-lg text-[#736c64] max-w-3xl mx-auto leading-relaxed">
              To bridge the gap between academic excellence and real-world business success by providing 
              sharp, intelligent, and actionable insights that drive meaningful growth and transformation.
            </p>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-[#1e1919] text-center mb-12 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
            Why Choose Us?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {achievements.map((achievement, index) => {
              const IconComponent = achievement.icon;
              return (
                <Card key={index} className="group hover:shadow-xl transition-all duration-500 transform hover:-translate-y-1 border-0 bg-white/80 backdrop-blur-sm">
                  <CardContent className="p-8">
                    <div className={`inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br ${achievement.color} items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-[#1e1919] mb-3 group-hover:text-[#61525a] transition-colors duration-300 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                      {achievement.title}
                    </h4>
                    <p className="text-[#736c64] leading-relaxed">
                      {achievement.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Team Section */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-[#1e1919] text-center mb-12 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
            Our Expert Team
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockTeamMembers.map((member) => (
              <Card key={member.id} className="group hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 border-0 bg-white/80 backdrop-blur-sm text-center">
                <CardContent className="p-8">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-full overflow-hidden ring-4 ring-[#5f9dff]/20 group-hover:ring-[#5f9dff]/50 transition-all duration-300">
                    <img 
                      src={member.avatar} 
                      alt={member.name} 
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <h4 className="text-xl font-bold text-[#1e1919] mb-2 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                    {member.name}
                  </h4>
                  <p className="text-[#5f9dff] font-semibold mb-3">{member.title}</p>
                  <div className="space-y-2 text-sm text-[#736c64]">
                    <p className="font-medium">{member.education}</p>
                    <p>{member.experience}</p>
                    <p className="text-[#61525a] font-semibold">{member.specialization}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Values */}
        <div>
          <h3 className="text-3xl font-bold text-[#1e1919] text-center mb-12 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
            Our Core Values
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="flex items-start space-x-6 p-8 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 hover:shadow-lg transition-all duration-300">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#5f9dff] to-[#61525a] flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-[#1e1919] mb-2 font-['Dbsharpgroteskvariable_Vf',_Arial,_sans-serif]">
                      {value.title}
                    </h4>
                    <p className="text-[#736c64] leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export { AboutSection };